import React, { useState, useEffect, useCallback } from "react";
import { Col, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE_COLORS = {
  primary: "#FF5B00",
  dark: "#e85a28",
  darker: "#d14d1f",
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = {
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
  background: toRgba(ORANGE_COLORS.primary, 0.08),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const iconGlyph = {
  color: ORANGE_COLORS.primary,
  fontSize: 18,
};

const CustomBadge = ({ children, style }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "999px",
      lineHeight: 1,
      whiteSpace: "nowrap",
      userSelect: "none",
      backgroundColor: "transparent",
      ...style,
    }}
  >
    {children}
  </span>
);

const SEVERITY_CONFIG = {
  critical: { label: "Critical", color: "#dc2626" },
  high:     { label: "High",     color: "#ef4444" },
  medium:   { label: "Medium",   color: "#f59e0b" },
  low:      { label: "Low",      color: "#10b981" },
  unknown:  { label: "Unknown",  color: "#9ca3af" },
};

const getAuthHeaders = () => {
  const authUser = JSON.parse(sessionStorage.getItem("authUser"));
  return {
    "Content-Type": "application/json",
    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
  };
};

const getSeverityKey = (severity, cvss) => {
  if (severity) {
    const key = severity.toString().toLowerCase();
    if (SEVERITY_CONFIG[key]) return key;
  }
  const s = parseFloat(cvss);
  if (!isNaN(s)) {
    if (s >= 9.0) return "critical";
    if (s >= 7.0) return "high";
    if (s >= 4.0) return "medium";
    return "low";
  }
  return "unknown";
};

const formatCVSS = (score) => {
  const num = parseFloat(score);
  return isNaN(num) ? "N/A" : num.toFixed(1);
};

const getRankBadgeStyle = (index) => {
  if (index === 0) return { backgroundColor: ORANGE_COLORS.primary, color: "#fff" };
  if (index === 1) return { backgroundColor: ORANGE_COLORS.dark,    color: "#fff" };
  if (index === 2) return { backgroundColor: ORANGE_COLORS.darker,  color: "#fff" };
  return { backgroundColor: "rgba(255, 107, 53, 0.15)", color: ORANGE_COLORS.primary };
};

const TopCVEsChart = () => {
  const [topCVEs, setTopCVEs]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchTopCVEs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/vulnerabilities/stats`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        sessionStorage.removeItem("authUser");
        window.location.href = "/login";
        return;
      }

      const data = await response.json();

      if (data?.success && Array.isArray(data.top_cves) && data.top_cves.length > 0) {
        setTopCVEs(data.top_cves);
      } else {
        setTopCVEs([]);
        if (!data?.success) setError("Failed to load CVE data");
      }
    } catch (err) {
      console.error("Error fetching CVEs:", err);
      setError("Could not reach server");
      setTopCVEs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopCVEs();
  }, [fetchTopCVEs]);

  return (
    <Col xl={6}>
      <Card style={cardShell}>
        <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <FeatherIcon icon="bar-chart-2" style={iconGlyph} />
            </span>
            Top 10 CVEs
          </h5>
          <button
            className="btn btn-sm"
            style={{ border: "1px solid rgba(0,0,0,0.15)", color: ORANGE_COLORS.primary }}
            onClick={fetchTopCVEs}
            disabled={loading}
          >
            <FeatherIcon icon="refresh-cw" size={16} />
          </button>
        </CardHeader>

        <CardBody
          className="custom-scrollbar"
          style={{ maxHeight: "450px", overflowY: "auto" }}
        >
          {loading ? (
            <div className="text-center py-5">
              <Spinner style={{ color: ORANGE_COLORS.primary }} />
              <p className="text-muted mt-2">Loading CVEs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <FeatherIcon icon="wifi-off" size={32} className="text-muted mb-2" />
              <p className="text-muted mb-2">{error}</p>
              <button className="btn btn-sm btn-outline-secondary" onClick={fetchTopCVEs}>
                Retry
              </button>
            </div>
          ) : topCVEs.length === 0 ? (
            <div className="text-center py-5">
              <FeatherIcon icon="shield" size={32} className="text-muted mb-2" />
              <p className="text-muted mb-0">No CVE data available</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {topCVEs.map((cve, index) => {
                const sevKey  = getSeverityKey(cve.severity, cve.cvss_score);
                const severity = SEVERITY_CONFIG[sevKey];
                return (
                  <div
                    key={cve.cve || index}
                    className="list-group-item px-0 py-3"
                    style={{
                      borderBottom: index < topCVEs.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
                      background: "transparent",
                    }}
                  >
                    <div className="d-flex">
                      {/* Rank */}
                      <div className="me-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                          style={{ width: 32, height: 32, fontSize: 13, ...getRankBadgeStyle(index) }}
                        >
                          {index + 1}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex-grow-1">
                        <span className="fw-semibold font-monospace d-block mb-2">
                          {cve.cve}
                        </span>
                        <div className="d-flex gap-2 mb-2">
                          <CustomBadge
                            style={{
                              color: severity.color,
                              border: `1px solid ${severity.color}`,
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "4px 10px",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                            }}
                          >
                            {severity.label}
                          </CustomBadge>
                          <CustomBadge
                            style={{
                              color: severity.color,
                              border: `1px solid ${severity.color}`,
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "4px 10px",
                            }}
                          >
                            CVSS: {formatCVSS(cve.cvss_score)}
                          </CustomBadge>
                        </div>
                        <div className="d-flex gap-3 text-muted small">
                          <span>
                            <strong>{parseInt(cve.occurrence_count) || 0}</strong> occurrences
                          </span>
                          <span>
                            <strong>{parseInt(cve.agents_affected) || 0}</strong> agents
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>

        <style>{`
          .custom-scrollbar::-webkit-scrollbar { width: 8px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(120,120,120,0.35); border-radius: 6px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(120,120,120,0.55); }
          .custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(120,120,120,0.45) transparent; }
        `}</style>
      </Card>
    </Col>
  );
};

export default TopCVEsChart;
